import { useState } from "react";
import { AlertTriangle, FileText, Link2, Lock, RefreshCw, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { formatBRL, formatarData, nomeMes } from "@/lib/utils";

// ============================================================
// F3 — Pagamento dos turnos (dentro da tela de Comissões)
// ============================================================
// O escalas PEDE, a clínica EXECUTA. Nenhum número desta caixa é calculado
// aqui: o apurado vem congelado da conferência (F2) e o pago vem do contas a
// pagar do ERP. A caixa mostra sempre as três linhas que importam —
// apurado, pago e saldo — porque é assim que se percebe um pagamento
// duplicado antes de ele virar dinheiro saindo duas vezes.
//
// Pagamento PARCIAL é o caso normal: os repasses de agosto começaram por
// fora, via FINPET. Por isso existe o campo "referência" (o id da transação
// lá) e o botão de vincular uma conta que JÁ está lançada no ERP — que não
// cria conta nova nenhuma.

const FORMAS: Array<{ valor: string; rotulo: string }> = [
  { valor: "pix", rotulo: "Pix" },
  { valor: "finpet", rotulo: "FINPET" },
  { valor: "dinheiro", rotulo: "Dinheiro" },
  { valor: "boleto", rotulo: "Boleto" },
  { valor: "cartao_credito", rotulo: "Cartão de crédito" },
  { valor: "cartao_debito", rotulo: "Cartão de débito" },
  { valor: "convenio", rotulo: "Convênio" },
  { valor: "outro", rotulo: "Outro" },
];

const ESTILO_STATUS: Record<string, string> = {
  aberto: "bg-amber-100 text-amber-800",
  parcial: "bg-sky-100 text-sky-800",
  pago: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-slate-200 text-slate-600",
};

function reais(centavos: number | null | undefined): string {
  return formatBRL((centavos ?? 0) / 100);
}

/** "1.234,56" ou "1234.56" → centavos inteiros. Vírgula manda, ponto é milhar. */
function paraCentavos(texto: string): number | null {
  const limpo = texto.trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  if (!limpo) return null;
  const n = Number(limpo);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

/**
 * Abre o PDF que a clínica devolveu em base64. Tenta uma aba nova; se o
 * navegador bloquear (o clique já passou por um await), cai no download —
 * o nome do arquivo vem do servidor, que é a fonte única desse nome.
 */
function abrirPdfBase64(base64: string, nomeArquivo: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const aba = window.open(url, "_blank");
  if (!aba) {
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeArquivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // 60s é folga suficiente para o visualizador carregar antes de o blob morrer.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function hojeISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

type Props = {
  profissionalId: number;
  nome: string;
  ano: number;
  mes: number;
  semVinculo: boolean;
  conferencia: { status: string; totalApuradoCentavos: number } | null;
};

export function PagamentoTurnos({ profissionalId, nome, ano, mes, semVinculo, conferencia }: Props) {
  const { isAdmin } = usePermissions();
  const utils = trpc.useUtils();

  const [empresa, setEmpresa] = useState<"holis" | "agropet">("holis");
  const [vencimento, setVencimento] = useState("");
  const [valor, setValor] = useState("");
  const [dataPag, setDataPag] = useState(hojeISO());
  const [forma, setForma] = useState("pix");
  const [contaId, setContaId] = useState("");
  const [referencia, setReferencia] = useState("");
  const [observacao, setObservacao] = useState("");
  const [abrirVinculo, setAbrirVinculo] = useState(false);

  const conferido = conferencia?.status === "conferido";

  const q = trpc.fechamentos.estado.useQuery(
    { ano, mes, profissionalId },
    { enabled: !semVinculo, retry: false },
  );
  const contas = trpc.fechamentos.contasBancarias.useQuery(undefined, {
    enabled: !semVinculo,
    retry: false,
  });
  const manuais = trpc.fechamentos.contasManuais.useQuery(
    { profissionalId },
    { enabled: !semVinculo && abrirVinculo, retry: false },
  );

  function recarregar() {
    utils.fechamentos.estado.invalidate();
    utils.fechamentos.contasManuais.invalidate();
    utils.comissoes.resumoPorProfissional.invalidate();
  }

  const fechar = trpc.fechamentos.fechar.useMutation({
    onSuccess: (r) => {
      recarregar();
      alert(
        r.jaExistia
          ? "Esta competência já tinha fechamento na clínica — nada foi duplicado."
          : `Conta a pagar criada na clínica: ${reais(r.fechamento?.totalApuradoCentavos)}, pendente.`,
      );
    },
    onError: (e) => alert(e.message),
  });

  const pagar = trpc.fechamentos.pagar.useMutation({
    onSuccess: (r) => {
      recarregar();
      setValor("");
      setReferencia("");
      setObservacao("");
      alert(
        r.jaExistia
          ? "Já havia um pagamento com esta referência — nada foi lançado de novo."
          : `Pagamento registrado. Saldo em aberto: ${reais(r.fechamento?.saldoCentavos)}.`,
      );
    },
    onError: (e) => alert(e.message),
  });

  const vincular = trpc.fechamentos.vincularContaManual.useMutation({
    onSuccess: (r) => {
      recarregar();
      setAbrirVinculo(false);
      alert(`Conta vinculada sem criar conta nova. Saldo em aberto: ${reais(r.fechamento?.saldoCentavos)}.`);
    },
    onError: (e) => alert(e.message),
  });

  const estornar = trpc.fechamentos.estornar.useMutation({
    onSuccess: () => {
      recarregar();
      alert("Estorno concluído na clínica.");
    },
    onError: (e) => alert(e.message),
  });

  // ── F5: recibo de um pagamento ────────────────────────────────────────
  // A primeira chamada EMITE (numera e congela na clínica); as seguintes
  // reimprimem o mesmo papel. Sem número = rascunho, e a tela diz por quê.
  const recibo = trpc.fechamentos.recibo.useMutation({
    onSuccess: (r) => {
      abrirPdfBase64(r.base64, r.nomeArquivo);
      if (!r.numero) {
        const porque =
          r.faltando.join(" | ") ||
          "Complete a ficha do prestador na clínica (CPF/CNPJ e endereço) e emita de novo.";
        alert(
          "O recibo saiu como RASCUNHO, sem número, e não vale como documento. " + porque,
        );
      }
    },
    onError: (e) => alert(e.message),
  });

  const ocupado =
    fechar.isPending ||
    pagar.isPending ||
    vincular.isPending ||
    estornar.isPending ||
    recibo.isPending ||
    q.isFetching;

  if (semVinculo) {
    return (
      <Bloco>
        <p className="text-sm text-slate-500">
          Sem vínculo com a ficha da clínica não há como gerar conta a pagar. Resolva em Vínculos.
        </p>
      </Bloco>
    );
  }

  if (q.isLoading) {
    return (
      <Bloco>
        <p className="text-sm text-slate-500">Consultando a clínica…</p>
      </Bloco>
    );
  }

  if (q.error) {
    return (
      <Bloco>
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{q.error.message}</span>
        </div>
      </Bloco>
    );
  }

  const estado = q.data;
  const f = estado?.fechamento ?? null;
  const pagamentos = (estado?.pagamentos ?? []).filter((p) => !p.estornadoEm);
  const estornados = (estado?.pagamentos ?? []).filter((p) => p.estornadoEm);
  const liberado = Boolean(estado?.ativo);

  function registrarPagamento() {
    if (!f) return;
    const centavos = paraCentavos(valor);
    if (!centavos) {
      alert("Informe o valor pago, em reais (ex.: 1.234,56).");
      return;
    }
    if (centavos > f.saldoCentavos) {
      alert(`O saldo em aberto é ${reais(f.saldoCentavos)}. Pagamento parcial pode; a mais, não.`);
      return;
    }
    pagar.mutate({
      profissionalId,
      fechamentoId: f.id,
      valorCentavos: centavos,
      data: dataPag,
      forma: forma as any,
      contaId: contaId ? Number(contaId) : null,
      referenciaExterna: referencia.trim() || null,
      observacao: observacao.trim() || null,
    });
  }

  return (
    <Bloco>
      {!liberado && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Pagamentos ainda não liberados pelo Bryan na clínica. Dá para conferir o que já existe,
            mas fechar competência, pagar e estornar ficam bloqueados até a chave ser ligada no ERP.
          </span>
        </div>
      )}

      {/* ── Sem fechamento ainda ─────────────────────────────────────────── */}
      {!f && (
        <>
          {!conferido ? (
            <p className="text-sm text-slate-500">
              A competência de {nomeMes(mes)} ainda não foi conferida com {nome}. Feche a conferência
              em Conferência — o valor a pagar nasce de lá, congelado.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Conferência aceita: <strong>{reais(conferencia?.totalApuradoCentavos)}</strong>. Fechar
                a competência cria, na clínica, uma conta a pagar <strong>pendente</strong> com esse
                valor. Nada é pago agora.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Campo rotulo="Empresa contratante">
                  <select
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={empresa}
                    onChange={(e) => setEmpresa(e.target.value as "holis" | "agropet")}
                  >
                    <option value="holis">Holis (48.205.445/0001-34)</option>
                    <option value="agropet">AgroPet (01.321.598/0001-83)</option>
                  </select>
                </Campo>
                <Campo rotulo="Vencimento (opcional)">
                  <Input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} />
                </Campo>
                <div className="flex items-end">
                  <Button
                    className="w-full"
                    disabled={!liberado || ocupado}
                    onClick={() => {
                      if (
                        !confirm(
                          `Fechar ${nomeMes(mes)}/${ano} de ${nome} e criar a conta a pagar de ${reais(
                            conferencia?.totalApuradoCentavos,
                          )} na clínica?`,
                        )
                      )
                        return;
                      fechar.mutate({
                        ano,
                        mes,
                        profissionalId,
                        empresa,
                        dataVencimento: vencimento || null,
                      });
                    }}
                  >
                    Fechar competência
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Com fechamento ───────────────────────────────────────────────── */}
      {f && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="font-medium text-slate-900">Fechamento #{f.id}</span>
              <span className={`rounded px-2 py-0.5 text-xs font-medium ${ESTILO_STATUS[f.status] ?? ""}`}>
                {f.status}
              </span>
              <span className="text-slate-400">·</span>
              <span>{f.qtdTurnos} turno(s)</span>
              {f.lancamentoId && (
                <>
                  <span className="text-slate-400">·</span>
                  <span>
                    conta a pagar #{f.lancamentoId}
                    {f.lancamentoStatus ? ` (${f.lancamentoStatus})` : ""}
                  </span>
                </>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={ocupado}>
              <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Numero rotulo="Apurado" valor={reais(f.totalApuradoCentavos)} />
            <Numero rotulo="Pago" valor={reais(f.totalPagoCentavos)} tom="text-emerald-700" />
            <Numero
              rotulo="Em aberto"
              valor={reais(f.saldoCentavos)}
              tom={f.saldoCentavos > 0 ? "text-slate-900" : "text-slate-400"}
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Pagamentos ({pagamentos.length})
            </div>
            {pagamentos.length === 0 ? (
              <p className="py-2 text-sm text-slate-400">Nenhum pagamento registrado.</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {pagamentos.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span className="text-slate-700">
                      {formatarData(p.data)} · {p.forma}
                      {p.referenciaExterna ? ` · ref. ${p.referenciaExterna}` : ""}
                      {p.origem === "conta_manual" && (
                        <Badge variant="outline" className="ml-2">
                          conta já lançada
                        </Badge>
                      )}
                      {p.lancamentoBaixaId ? (
                        <span className="ml-2 text-xs text-slate-400">
                          lançamento #{p.lancamentoBaixaId}
                          {p.lancamentoStatus ? ` (${p.lancamentoStatus})` : ""}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-medium text-slate-900">{reais(p.valorCentavos)}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={ocupado}
                        title="Emite (ou reimprime) o recibo deste pagamento. Sem CPF/CNPJ e endereco na ficha da clinica, o PDF sai como rascunho sem numero."
                        onClick={() => recibo.mutate({ profissionalId, pagamentoId: p.id })}
                      >
                        <FileText className="mr-1 h-3.5 w-3.5" />
                        Recibo
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!liberado || ocupado}
                          onClick={() => {
                            const motivo = prompt(
                              `Por que estornar o pagamento de ${reais(p.valorCentavos)} de ${nome}?`,
                            );
                            if (!motivo || motivo.trim().length < 5) return;
                            estornar.mutate({
                              profissionalId,
                              fechamentoId: f.id,
                              nivel: "pagamento",
                              pagamentoId: p.id,
                              motivo: motivo.trim(),
                            });
                          }}
                        >
                          <Undo2 className="mr-1 h-3.5 w-3.5" />
                          Estornar
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {estornados.length > 0 && (
              <p className="mt-2 text-xs text-slate-400">
                {estornados.length} pagamento(s) estornado(s) continuam no histórico da clínica.
              </p>
            )}
          </div>

          {/* ── Registrar pagamento ───────────────────────────────────────── */}
          {f.status !== "cancelado" && f.saldoCentavos > 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Registrar pagamento (parcial pode)
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Campo rotulo="Valor (R$)">
                  <Input
                    inputMode="decimal"
                    placeholder={(f.saldoCentavos / 100).toFixed(2).replace(".", ",")}
                    value={valor}
                    onChange={(e) => setValor(e.target.value)}
                  />
                </Campo>
                <Campo rotulo="Data do pagamento">
                  <Input type="date" value={dataPag} onChange={(e) => setDataPag(e.target.value)} />
                </Campo>
                <Campo rotulo="Forma">
                  <select
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={forma}
                    onChange={(e) => setForma(e.target.value)}
                  >
                    {FORMAS.map((o) => (
                      <option key={o.valor} value={o.valor}>
                        {o.rotulo}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo rotulo="Conta (opcional)">
                  <select
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={contaId}
                    onChange={(e) => setContaId(e.target.value)}
                  >
                    <option value="">—</option>
                    {(contas.data?.itens ?? []).map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo rotulo="Referência (ex.: id FINPET)">
                  <Input value={referencia} onChange={(e) => setReferencia(e.target.value)} />
                </Campo>
                <Campo rotulo="Observação">
                  <Input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
                </Campo>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button disabled={!liberado || ocupado} onClick={registrarPagamento}>
                  Registrar pagamento
                </Button>
                <Button
                  variant="outline"
                  disabled={!liberado || ocupado}
                  onClick={() => setAbrirVinculo((v) => !v)}
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  Vincular conta manual
                </Button>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                A referência é o que impede pagar duas vezes a mesma transação: reenviar a mesma
                referência devolve o pagamento que já existe, em vez de criar outro.
              </p>
            </div>
          )}

          {/* ── Vincular conta que já existe no ERP ───────────────────────── */}
          {abrirVinculo && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Contas já lançadas na clínica em nome de {nome}
              </div>
              {manuais.isLoading && <p className="text-sm text-slate-500">Procurando…</p>}
              {manuais.error && <p className="text-sm text-red-700">{manuais.error.message}</p>}
              {manuais.data && manuais.data.itens.length === 0 && (
                <p className="text-sm text-slate-500">
                  Nenhuma conta solta em nome dele. É o esperado quando tudo já foi vinculado.
                </p>
              )}
              <ul className="divide-y divide-slate-200 text-sm">
                {(manuais.data?.itens ?? []).map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span className="text-slate-700">
                      {c.dataPagamento
                        ? formatarData(c.dataPagamento)
                        : c.dataVencimento
                          ? formatarData(c.dataVencimento)
                          : "sem data"}{" "}
                      · {c.descricao} <Badge variant="outline">{c.status}</Badge>
                      {c.pareceComissao && (
                        <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          lançada à mão
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-medium text-slate-900">{reais(c.valorCentavos)}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!liberado || ocupado || c.valorCentavos > f.saldoCentavos}
                        onClick={() => {
                          if (
                            !confirm(
                              `Vincular a conta #${c.id} (${reais(
                                c.valorCentavos,
                              )}) a este fechamento? Nenhuma conta nova é criada — o saldo em aberto encolhe pelo mesmo valor.`,
                            )
                          )
                            return;
                          vincular.mutate({
                            profissionalId,
                            fechamentoId: f.id,
                            lancamentoId: c.id,
                          });
                        }}
                      >
                        Vincular
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                Contas maiores que o saldo em aberto ficam desabilitadas de propósito: vincular
                pagaria mais do que o apurado. Confira a competência antes.
              </p>
            </div>
          )}

          {/* ── Cancelar a competência (admin) ────────────────────────────── */}
          {isAdmin && f.status !== "cancelado" && (
            <div className="border-t border-slate-100 pt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={!liberado || ocupado || pagamentos.length > 0}
                onClick={() => {
                  const motivo = prompt(
                    `Por que cancelar o fechamento de ${nomeMes(mes)}/${ano} de ${nome}? A conta a pagar sai do financeiro.`,
                  );
                  if (!motivo || motivo.trim().length < 5) return;
                  estornar.mutate({
                    profissionalId,
                    fechamentoId: f.id,
                    nivel: "fechamento",
                    motivo: motivo.trim(),
                  });
                }}
              >
                Cancelar competência
              </Button>
              {pagamentos.length > 0 && (
                <span className="ml-2 text-xs text-slate-500">
                  Estorne os pagamentos antes de cancelar a competência.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Bloco>
  );
}

function Bloco({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Pagamento dos turnos
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-3">{children}</div>
    </div>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-slate-500">{rotulo}</span>
      {children}
    </label>
  );
}

function Numero({ rotulo, valor, tom }: { rotulo: string; valor: string; tom?: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{rotulo}</div>
      <div className={`text-lg font-semibold ${tom ?? "text-slate-900"}`}>{valor}</div>
    </div>
  );
}
